import * as React from 'react';
import { AppDispatch } from '@app/store';
import UserSession from '@app/common/user-session';
import ApiKeyApi from '@app/api/apikey/apikey.actions';
import { textInputRequiredOnly, textInputValidate, isValidNumber, isWithinRange } from '@app/lib/validator';
import { Saver } from '@app/components/Saver';
import { sortable, cellWidth } from '@patternfly/react-table';
import { ExclamationCircleIcon, PencilAltIcon, BullhornIcon, PlusIcon } from '@patternfly/react-icons';
import { Modal, ModalVariant, PageSection, Title, TextInput, Flex, FlexItem, Form, FormGroup, Spinner, TextArea, ToggleGroup, ToggleGroupItem, Radio, Button as PFButton } from '@patternfly/react-core';
import './Admin.css';
import { connect } from 'react-redux';
import { State } from '@app/store/root-reducer';
import { Card, CardBody, Button, CardHeader, Col } from 'reactstrap';
import { Column, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, Updater, useReactTable } from '@tanstack/react-table';
import { MdArrowDownward, MdArrowUpward, MdUnfoldMore } from 'react-icons/md';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { actionCreators as sessionActionCreators } from '@app/common/session/session.reducer';

type ColumnFiltersState = ColumnFilter[];
interface ColumnFilter {
  id: string;
  value: unknown;
}

export const ApiKeys: React.FunctionComponent = ({}) => {
  const dispatch = useDispatch();
  const emptyApiKey = { name: '', days_valid_for: '90', scope: 'ReadOnly', status: 'Active' };
  const defaultScope = { ReadOnly: true, ReadWrite: false, All: false };
  
  const [loading, setLoading] = React.useState(true);
  const [apiKeys, setApiKeys] = React.useState([]);
  const [apiKeysData, setApiKeysData] = React.useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);

  const [actionId, setActionId] = React.useState("");
  const [keyName, setKeyName] = React.useState("");
  const [deleteKeyName, setDeleteKeyName] = React.useState("");

  const [page, setPage] = React.useState(1);
  const [apiKey, setApiKey] = React.useState(emptyApiKey);
  const [scope, setScope] = React.useState(defaultScope);
  const [secretKey, setSecretKey] = React.useState("");
  const [validUntil, setValidUntil] = React.useState("");
  const [secretKeyMessage, setSecretKeyMessage] = React.useState("");
  const [buttonText, setButtonText] = React.useState("Create");
  const [cancelText, setCancelText] = React.useState("Cancel");
  const [isCreating, setIsCreating] = React.useState(false);
  const [error, setError] = React.useState("");

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

  const loadApiKeys = async () => {
    try {
      const userId = UserSession.getParam('userId');
      const tokenList = await ApiKeyApi.getAll(userId);
      let tokenRows = [];
      let tokenDataRows = [];

      if (tokenList) {
        tokenRows = tokenList.map(item => {
          return apiKeyFromItem(item);
        });
        tokenDataRows = tokenList.map(item => {
          return apiKeyDataFromItem(item);
        });
      }

      setApiKeys(tokenRows);
      setApiKeysData(tokenDataRows);
      setLoading(false);
    }
    catch (error) {
      console.log(error);
      setApiKeys([]);
      setApiKeysData([]);
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadApiKeys();
  }, []);

  const columns = React.useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'access_key',
      header: 'Access Key ID',
      enableColumnFilter: false,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableColumnFilter: false,
    },
    {
      accessorKey: 'not_valid_after',
      header: 'Valid Until',
      enableColumnFilter: false,
    },
    {
      header: 'Actions',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <>
          <Button 
            className="ethica-button-green" 
            size="sm me-2"
            onClick={(e) => showUpdateStatusModal(e, null, {
              id: { title: row.original.id },
              status: { title: row.original.status },
              name: { title: row.original.name }
            })}
          >
            Update Status
          </Button>
          <Button 
            className="ethica-button-black" 
            size="sm"
            onClick={(e) => showDeleteConfirmation(row.original.id, row.original.name)}
          >
            Delete
          </Button>
        </>
      ),
    },
  ], []);

  const table = useReactTable({
    data: apiKeysData,
    columns,
    filterFns: {},
    state: {
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
    },
  });

  const getColumns = () => {
    return [
      { title: 'Name', transforms: [sortable] }, 
      { title: 'Access Key ID' }, 
      { title: 'Status' },
      { title: 'Valid Until', transforms: [sortable] },
      { title: 'ID', props: { className: 'pf-m-hidden pf-m-hidden-on-md' }},
      { title: 'Actions', transforms: [cellWidth(10)] }
    ];
  }

  const getColumnIndex = (columnName) => {
    const columns = getColumns();
    for (var i = 0; i < columns.length; i++) {
      if (columns[i]['title'] === columnName) {
        return i;
      }
    }
    return -1;
  }

  const apiKeyFromItem = (item) => {
    return {
      cells: [
        { title: item.name },
        { title: item.access_key },
        { title: item.status },
        { title: item.not_valid_after },
        { title: item.id, props: { className: 'pf-m-hidden pf-m-hidden-on-md' }}
      ]
    }
  }

  const apiKeyDataFromItem = (item) => {
    return {
      id: item.id,
      name: item.name,
      access_key: item.access_key,
      status: item.status,
      not_valid_after: item.not_valid_after
    }
  }

  const showDeleteConfirmation = (tokenId, name) => {
    Swal.fire({
      title: 'Confirm Action',
      text: `Are you sure you want to delete this Personal Access Token?\n${name}`,
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setActionId(tokenId);
        setDeleteKeyName(name);
        deleteRow(tokenId);
      }
    });
  };

  const deleteRow = async (tokenId) => {
    const userId = UserSession.getParam('userId');

    try {
      const result = await ApiKeyApi.delete(userId, tokenId);
      
      try {
        await loadApiKeys();
        
        dispatch(sessionActionCreators.notificationEnqueue({
          message: 'Personal Access Token Deleted',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }));
      } catch (refreshError) {

        const updatedData = apiKeysData.filter(t => t.id !== tokenId);
        const updatedKeys = apiKeys.filter(keyRow => keyRow.cells[4].title !== tokenId);
        setApiKeysData([ ...updatedData ]);
        setApiKeys([ ...updatedKeys ]);
        console.log('Failed to refresh from server, using local filtering:', refreshError);
      }
      
      setActionId("");
      setPage(1);
      setError('');
    }
    catch(error) {
      Swal.fire({
        title: 'Error',
        text: 'There was an error deleting the Personal Access Token',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      console.log(error)
    }
  }

  const handleChange = (name, value) => {
    setApiKey(prev => ({ ...prev, [name]: value }));
  };

  const handleScopeChange = (e, value) => {
    const updatedScope = { ReadOnly: false, ReadWrite: false, All: false };
    updatedScope[value] = true;
    setScope(updatedScope);
  };



  const handleAddApiKeyToggle = () => {
    setIsCreateModalOpen(!isCreateModalOpen);
    setApiKey(emptyApiKey);
    setScope(defaultScope);
  };



  const showCloseConfirmation = () => {
    Swal.fire({
      title: 'Confirm Action',
      text: 'Are you sure you want to close this window?',
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        closeAllDialogs();
      }
    });
  };

  const closeAllDialogs = () => {
    setIsCreateModalOpen(false);
  };

  const addApiKey = () => {
    setIsCreateModalOpen(!isCreateModalOpen);
    setSecretKey('');
    setValidUntil('');
    setSecretKeyMessage('');
    setButtonText('Create');
    setCancelText('Cancel');
    setError('');
  };

  const showUpdateStatusModal = (event, rowId, rowData) => {
    const tokenId = rowData['id']['title'];
    const tokenStatus = rowData['status']['title'];
    const tokenName = rowData['name']['title'];
    const statusAction = tokenStatus === 'Active' ? 'Disable' : 'Enable';

    Swal.fire({
      title: 'Update Personal Access Token Status',
      text: `Are you sure you want to ${statusAction.toLowerCase()} the ${tokenName} Personal Access Token?`,
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: statusAction,
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        updateStatus(tokenId, statusAction);
      }
    });
  };

  const updateStatus = async (tokenId, statusAction) => {
    const userId = UserSession.getParam('userId');
    const tokenData = { status: statusAction === 'Enable' ? 'Active' : 'Disabled' };
    
    try {
      const result = await ApiKeyApi.updateStatus(userId, tokenId, tokenData);
      
      try {
        await loadApiKeys();
        
        dispatch(sessionActionCreators.notificationEnqueue({
          message: `Personal Access Token ${statusAction}d Successfully`,
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }));
      } catch (refreshError) {

        const updatedData = apiKeysData.map(token => {
          if (token.id == tokenId) {
            return { ...token, status: tokenData.status };
          }
          return token;
        });

        const updatedKeys = apiKeys.map(keyRow => {
          if (keyRow.cells[4].title == tokenId) {
            const updatedCells = [...keyRow.cells];
            updatedCells[2] = { title: tokenData.status };
            return { ...keyRow, cells: updatedCells };
          }
          return keyRow;
        });

        setApiKeysData(updatedData);
        setApiKeys(updatedKeys);

        
        dispatch(sessionActionCreators.notificationEnqueue({
          message: `Personal Access Token ${statusAction}d Successfully`,
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }));
      }
    }
    catch(error) {

      Swal.fire({
        title: 'Error',
        text: `There was an error ${statusAction.toLowerCase()}ing the Personal Access Token`,
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleCreate = async (event) => {
    if (event) {
      event.preventDefault();
    }

    if (secretKey !== '') {
      closeAllDialogs();
      return;
    }

    setIsCreating(true);
    
    try {
      const userId = UserSession.getParam('userId');
      const scopeValue = Object.keys(scope).find(key => scope[key]);
      const payload = {
        name: apiKey.name,
        days_valid_for: parseInt(apiKey.days_valid_for),
        scope: scopeValue
      };

      const result = await ApiKeyApi.create(userId, payload);

      if (result?.data?.token?.access_key && result?.data?.token?.secret_key) {
        setSecretKey(result.data.token.secret_key);
        setValidUntil(result.data.token.not_valid_after);
        setSecretKeyMessage(`This token will be displayed only once. Please copy and store it securely.`);
        
        // Add new key to the list
        const newKey = {
          id: result.data.token.id,
          name: result.data.token.name,
          access_key: result.data.token.access_key,
          status: result.data.token.status || 'Active',
          not_valid_after: result.data.token.not_valid_after
        };
        
        setApiKeysData(prev => [...prev, newKey]);
        setApiKeys(prev => [...prev, apiKeyFromItem(newKey)]);
        setButtonText('Close');
        setCancelText('')
        setError('');
      } else {
        setError('Invalid response from server');
      }
      setIsCreating(false);
    }
    catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setIsCreating(false);
      setError('There was an error creating the Personal Access Token: ' + errorMessage);
    }
  }
  

  const nameIsValid = apiKey.name && apiKey.name.length < 256;
  const nameErrorState = nameIsValid ? { validated: 'default', errorText: '' } : { validated: 'error', errorText: 'Required field' }
  const daysIsValid = isValidNumber(apiKey.days_valid_for) && isWithinRange(parseInt(apiKey.days_valid_for), 1, 365);
  const daysErrorState = daysIsValid ? { validated: 'default', errorText: '' } : { validated: 'error', errorText: 'Must be a number between 1-365' }
  const formValid = nameErrorState.validated !== 'error' && daysErrorState.validated !== 'error';

  return (
    <React.Fragment>
      <div className="controllers-card-wrapper">
        <Card className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0"><strong>Personal Access Tokens</strong></h2>
            <Button 
              className="ethica-button-green" 
              onClick={addApiKey}
              style={{ whiteSpace: 'nowrap' }}
            >
              <PlusIcon className="me-1" /> Add PAT
            </Button>
          </div>

          {loading ? 
              <Spinner size="lg" />
            : apiKeysData?.length === 0 ? (
              <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th>
                      <div className="d-flex align-items-center justify-content-start">
                        <strong>Name</strong>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex align-items-center justify-content-start">
                        <strong>Access Key ID</strong>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex align-items-center justify-content-start">
                        <strong>Status</strong>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex align-items-center justify-content-start">
                        <strong>Valid Until</strong>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex align-items-center justify-content-center">
                        <strong>Actions</strong>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={5} className="text-center">No Personal Access Tokens found!</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th key={header.id} colSpan={header.colSpan}>
                          {header.isPlaceholder ? null : (
                            <div className={`d-flex align-items-center ${header.column.columnDef.header === 'Name' || header.column.columnDef.header === 'Access Key ID' || header.column.columnDef.header === 'Valid Until' || header.column.columnDef.header === 'Status' ? 'justify-content-start' : (header.column.getCanFilter() ? 'justify-content-start' : 'justify-content-center')}`}>
                              <div
                                {...{
                                  className: header.column.getCanSort()
                                    ? 'select-none table-column-header'
                                    : 'table-column-header',
                                  onClick: header.column.getToggleSortingHandler(),
                                }}
                              >
                                <strong>{flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}</strong>

                                {header.column.getCanSort() && (
                                  {
                                    asc: <MdArrowUpward className="ms-2" />,
                                    desc: <MdArrowDownward className="ms-2" />,
                                  }[header.column.getIsSorted() as string] ?? (
                                    <MdUnfoldMore className="ms-2 opacity-50" />
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map(row => (
                    <React.Fragment key={row.id}>
                        <tr>
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id} className={cell.column.columnDef.header === 'Name' || cell.column.columnDef.header === 'Access Key ID' || cell.column.columnDef.header === 'Valid Until' || cell.column.columnDef.header === 'Status' ? 'text-start' : (cell.column.columnDef.header === 'Actions' ? 'text-center' : (cell.column.getCanFilter() ? '' : 'text-center'))}>
                            <span className={`d-flex flex-wrap-nowrap w-100 ${cell.column.columnDef.header === 'Name' || cell.column.columnDef.header === 'Access Key ID' || cell.column.columnDef.header === 'Valid Until' || cell.column.columnDef.header === 'Status' ? 'justify-content-start' : 'justify-content-center'}`}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                          </td>
                        ))}
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
        </Card>
      </div>

      <Modal
        title="Create Personal Access Token"
        titleIconVariant={BullhornIcon}
        variant={ModalVariant.small}
        isOpen={isCreateModalOpen}
        onClose={handleAddApiKeyToggle}
        actions={secretKey !== '' ? 
          [
            <Button 
              key="confirm" 
              variant="primary" 
              className="ethica-button-green" 
              onClick={handleCreate}
            >
              {buttonText}
            </Button>
          ] : 
          [
            <Button 
              key="confirm" 
              variant="primary" 
              className="ethica-button-green"
              isDisabled={!formValid} 
              isLoading={isCreating} 
              onClick={handleCreate}
            >
              {buttonText}
            </Button>,
            <Button 
              key="cancel" 
              variant="primary"
              className="ethica-button-cancel" 
              onClick={cancelText === '' ? showCloseConfirmation : handleAddApiKeyToggle}
            >
              {cancelText}
            </Button>
          ]
        }
      >
        <Form isHorizontal>
          <Flex>
            <FlexItem>
              <FormGroup label="Name" isRequired fieldId="name" className="form-item"
                helperTextInvalid={nameErrorState.errorText} helperTextInvalidIcon={<ExclamationCircleIcon />}>
                <TextInput
                  isRequired type="text" id="name" name="name"
                  value={apiKey.name} onChange={(e) => handleChange("name", e)} className="form-control"
                />
              </FormGroup>
              <FormGroup label="Days valid for" isRequired fieldId="days_valid_for" className="form-item"
                helperTextInvalid={daysErrorState.errorText} helperTextInvalidIcon={<ExclamationCircleIcon />}>
                <TextInput
                  isRequired type="text" id="days_valid_for" name="days_valid_for"
                  value={apiKey.days_valid_for} onChange={(e) => handleChange("days_valid_for", e)} className="form-control"
                />
              </FormGroup>
              <br />
              <FormGroup label="Scope" fieldId="scope" className="form-item">
                <Radio id="readOnly" label="Read Only" name="scope-group"
                  isChecked={scope.ReadOnly} onChange={(e) => handleScopeChange(e, 'ReadOnly')}
                />
                <Radio 
                  id="readWrite" label="Read Write" name="scope-group"
                  isChecked={scope.ReadWrite} onChange={(e) => handleScopeChange(e, 'ReadWrite')}
                />
                <Radio 
                  id="all" label="All" name="scope-group"
                  isChecked={scope.All} onChange={(e) => handleScopeChange(e, 'All')}
                />
              </FormGroup>

              { error !== '' ? <p><br />{ error }</p> : <></> }

              { secretKey !== '' ?
                <React.Fragment>
                  <br />
                  <h1><strong>Secret Key</strong></h1>
                  {secretKeyMessage}<br /><br />
                  <b>Valid Until:</b> {validUntil}<br /><br />
                  <Card isCompact className="secret-key">
                    <CardBody>
                      {secretKey}
                    </CardBody>
                  </Card>
                </React.Fragment>
              : <></> }
            </FlexItem>
          </Flex>
        </Form>
      </Modal>


    </React.Fragment>
  );
}

export default ApiKeys;